import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import { Skeleton } from "../components/ui/skeleton";
import PageHeader from "../components/layout/PageHeader";
import { DOA_CATEGORIES, DUAS, getDoasByCategory } from "../services/doaService";
import { useLanguage } from "../App";
import {
  Search,
  Sunrise,
  Sunset,
  Moon,
  Utensils,
  Plane,
  Calendar,
  ChevronRight,
  Heart,
  Shield,
  HeartPulse,
  Star,
  BookOpen,
  Sparkles,
  List,
  Filter,
  BookMarked,
  Clock
} from "lucide-react";

const iconMap = {
  sunrise: Sunrise,
  sunset: Sunset,
  moon: Moon,
  utensils: Utensils,
  plane: Plane,
  calendar: Calendar,
  mosque: Heart,
  shield: Shield,
  "heart-pulse": HeartPulse,
  star: Star,
  "book-open": BookOpen,
};

// Custom scrollbar styles
const tabsContainerStyle = `
  .tabs-scroll-container::-webkit-scrollbar {
    height: 6px;
  }
  .tabs-scroll-container::-webkit-scrollbar-track {
    background: transparent;
  }
  .tabs-scroll-container::-webkit-scrollbar-thumb {
    background: rgb(203 213 225 / 0.5);
    border-radius: 3px;
  }
  .tabs-scroll-container::-webkit-scrollbar-thumb:hover {
    background: rgb(203 213 225 / 0.8);
  }
  .tabs-scroll-container {
    -ms-overflow-style: thin;
    scrollbar-width: thin;
  }
`;

const Doa = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    // Get category from URL or default to "all"
    return searchParams.get('category') || "all";
  });

  // Update URL when category changes
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSearchParams(category === 'all' ? {} : { category });
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(id);
  }, []);

  const filteredDuas = getDoasByCategory(selectedCategory).filter((doa) => {
    if (!search) return true;
    return (
      doa.title.toLowerCase().includes(search.toLowerCase()) ||
      doa.titleEn.toLowerCase().includes(search.toLowerCase()) ||
      doa.arabic.includes(search)
    );
  });

  const morningDzikir = DUAS.filter(d => d.category === "morning").slice(0, 1)[0];

  // Get tab badge counts
  const getTabBadge = (category) => {
    if (category === "all") return DUAS.length;
    return getDoasByCategory(category).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="doa-loading">
        <PageHeader title={isEnglish ? "Dua & Dhikr" : "Doa & Dzikir"} />
        <div className="page-container space-y-4 sm:space-y-5">
          {/* Stats Cards Skeleton */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
                  <Skeleton className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mx-auto mb-1.5 sm:mb-2" />
                  <Skeleton className="h-5 w-16 sm:h-6 sm:w-20 mx-auto mb-0.5 sm:mb-1" />
                  <Skeleton className="h-2.5 w-12 sm:h-3 sm:w-16 mx-auto" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Search Skeleton */}
          <div className="relative">
            <Skeleton className="h-10 sm:h-12 md:h-14 w-full rounded-lg sm:rounded-xl" />
          </div>

          {/* Featured Cards Skeleton */}
          <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-3">
            <Card className="islamic-gradient text-white">
              <CardContent className="p-3 sm:p-4 md:p-5 space-y-2 sm:space-y-3">
                <Skeleton className="h-5 w-28 sm:h-6 sm:w-32 bg-white/30" />
                <Skeleton className="h-5 w-44 sm:h-6 sm:w-52 bg-white/30" />
                <Skeleton className="h-3 w-24 sm:h-4 sm:w-28 bg-white/30" />
                <Skeleton className="h-5 w-full sm:h-6 bg-white/20" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <CardContent className="p-3 sm:p-4 md:p-5">
                <Skeleton className="h-5 w-28 sm:h-6 sm:w-32 bg-white/30" />
                <Skeleton className="h-5 w-44 sm:h-6 sm:w-52 bg-white/30" />
                <Skeleton className="h-3 w-full sm:h-4 bg-white/20" />
              </CardContent>
            </Card>
          </div>

          {/* Premium Tabs Skeleton */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background p-1">
              <Skeleton className="h-10 sm:h-12 w-full" />
            </div>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <div className="space-y-2 sm:space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Card key={index}>
                    <CardContent className="p-3 sm:p-4 md:p-5">
                      <div className="flex items-start gap-2.5 sm:gap-3 md:gap-4">
                        <Skeleton className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 rounded-xl sm:rounded-2xl" />
                        <div className="flex-1 space-y-1.5 sm:space-y-2">
                          <Skeleton className="h-4 w-36 sm:h-5 sm:w-40" />
                          <Skeleton className="h-3 w-28 sm:h-4 sm:w-32" />
                          <Skeleton className="h-3 w-full sm:h-4" />
                        </div>
                        <Skeleton className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="doa-page">
      <style>{tabsContainerStyle}</style>
      <PageHeader title={isEnglish ? "Dua & Dhikr" : "Doa & Dzikir"} />

      <div className="page-container space-y-4 sm:space-y-5">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
              <BookMarked className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-primary" />
              <p className="text-xl sm:text-2xl font-bold">{DUAS.length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{isEnglish ? "Total Duas" : "Total Doa"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
              <Filter className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-primary" />
              <p className="text-xl sm:text-2xl font-bold">{DOA_CATEGORIES.length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{isEnglish ? "Categories" : "Kategori"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
              <Clock className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-primary" />
              <p className="text-xl sm:text-2xl font-bold">{getDoasByCategory("daily").length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{isEnglish ? "Daily Dhikr" : "Dzikir Harian"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
              <Star className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-primary" />
              <p className="text-xl sm:text-2xl font-bold">{filteredDuas.length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{isEnglish ? "Showing" : "Tampilan"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
          <Input
            placeholder={isEnglish ? "Search dua or dhikr..." : "Cari doa atau dzikir..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 sm:pl-12 h-10 sm:h-12 md:h-14 text-sm sm:text-base border-2"
            data-testid="doa-search"
          />
        </div>

        {/* Featured Cards Grid */}
        <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-3">
          {/* Daily Dzikir Card */}
          {morningDzikir && (
            <Card
              className="islamic-gradient text-white cursor-pointer hover:scale-[1.01] sm:hover:scale-[1.02] transition-all duration-300 shadow-lg sm:shadow-xl"
              onClick={() => navigate(`/doa/${morningDzikir.id}`)}
              data-testid="daily-dzikir-card"
            >
              <CardContent className="p-3 sm:p-4 md:p-6">
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  <Badge className="bg-white/20 text-white border-0 text-[10px] sm:text-xs px-2 py-0.5">
                    {isEnglish ? "Dhikr of the Day" : "Dzikir Hari Ini"}
                  </Badge>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold mb-1.5 sm:mb-2">{isEnglish ? morningDzikir.titleEn : morningDzikir.title}</h3>
                <p className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-3">
                  {morningDzikir.repeat > 1
                    ? (isEnglish ? `Read ${morningDzikir.repeat}x` : `Dibaca ${morningDzikir.repeat}x`)
                    : (isEnglish ? "Read 1x" : "Dibaca 1x")}
                </p>
                <p className="font-arabic text-base sm:text-lg text-right line-clamp-2" dir="rtl">
                  {morningDzikir.arabic}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Tasbih Digital Card */}
          <Card
            className="bg-gradient-to-br from-primary to-primary/80 text-white cursor-pointer hover:scale-[1.01] sm:hover:scale-[1.02] hover:from-primary/90 hover:to-primary transition-all duration-300 shadow-lg sm:shadow-xl"
            onClick={() => navigate("/doa/tasbih")}
          >
            <CardContent className="p-3 sm:p-4 md:p-6">
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <Badge className="bg-white/20 text-white border-0 text-[10px] sm:text-xs px-2 py-0.5">
                  {isEnglish ? "Digital Tasbih" : "Tasbih Digital"}
                </Badge>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 text-xl sm:text-2xl">
                  📿
                </div>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold mb-1.5 sm:mb-2">
                {isEnglish ? "Morning & Evening Dhikr" : "Dzikir Pagi & Petang"}
              </h3>
              <p className="text-white/80 text-xs sm:text-sm">
                {isEnglish
                  ? "SubhanAllah 33x • Alhamdulillah 33x • Allahu Akbar 34x"
                  : "SubhanAllah 33x • Alhamdulillah 33x • Allahu Akbar 34x"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Premium Tabs */}
        <Card className="overflow-visible sm:overflow-hidden">
          {/* Premium Tab Header */}
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background -m-1 p-1 rounded-t-lg">
            <div className="tabs-scroll-container flex gap-1 overflow-x-auto overflow-y-hidden scroll-smooth" style={{
              scrollbarWidth: 'thin',
              WebkitOverflowScrolling: 'touch'
            }}>
              {/* All Tab */}
              <button
                onClick={() => handleCategoryChange("all")}
                className={`flex-shrink-0 relative flex items-center justify-center gap-1 sm:gap-2 px-1.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 rounded-lg sm:rounded-xl font-medium text-[10px] sm:text-xs md:text-sm transition-all duration-300 min-w-fit ${
                  selectedCategory === "all"
                    ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-md sm:shadow-lg shadow-primary/25"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                {selectedCategory === "all" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-lg sm:rounded-xl animate-pulse" />
                )}
                <List className={`w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 ${selectedCategory === "all" ? "text-white" : ""} flex-shrink-0`} />
                <span className="relative truncate">{isEnglish ? "Semua" : "Semua"}</span>
                <span
                  className={`ml-0.5 sm:ml-1 px-1 sm:px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-medium flex-shrink-0 ${
                    selectedCategory === "all"
                      ? "bg-white/20 text-white border-white/30"
                      : "bg-primary/10 text-primary border-primary/20"
                  }`}
                >
                  {getTabBadge("all")}
                </span>
                {selectedCategory === "all" && (
                  <Sparkles className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-400 animate-pulse" />
                )}
              </button>

              {/* Category Tabs */}
              {DOA_CATEGORIES.map((cat) => {
                const Icon = iconMap[cat.icon] || Calendar;
                const isActive = selectedCategory === cat.id;

                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex-shrink-0 relative flex items-center justify-center gap-1 sm:gap-2 px-1.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 rounded-lg sm:rounded-xl font-medium text-[10px] sm:text-xs md:text-sm transition-all duration-300 min-w-fit ${
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-md sm:shadow-lg shadow-primary/25"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-lg sm:rounded-xl animate-pulse" />
                    )}
                    <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 ${isActive ? "text-white" : ""} flex-shrink-0`} />
                    <span className="relative truncate">
                      {isEnglish ? cat.englishName : cat.name}
                    </span>
                    <span
                      className={`ml-0.5 sm:ml-1 px-1 sm:px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-medium flex-shrink-0 ${
                        isActive
                          ? "bg-white/20 text-white border-white/30"
                          : "bg-primary/10 text-primary border-primary/20"
                      }`}
                    >
                      {getTabBadge(cat.id)}
                    </span>
                    {isActive && (
                      <Sparkles className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-400 animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <CardContent className="p-3 sm:p-4 md:p-6">
            {/* Doa List */}
            <div className="space-y-2 sm:space-y-3">
              {filteredDuas.length === 0 ? (
                <Card>
                  <CardContent className="p-8 sm:p-12 text-center text-muted-foreground">
                    <BookMarked className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-50" />
                    <p className="font-medium text-sm sm:text-base">{isEnglish ? "No dua found" : "Doa tidak ditemukan"}</p>
                    <p className="text-xs sm:text-sm mt-2">{isEnglish ? "Try a different search term or category" : "Coba kata kunci atau kategori lain"}</p>
                  </CardContent>
                </Card>
              ) : (
                filteredDuas.map((doa) => (
                  <Card
                    key={doa.id}
                    className="cursor-pointer hover:shadow-md sm:hover:shadow-lg hover:scale-[1.005] sm:hover:scale-[1.01] transition-all duration-300 group"
                    onClick={() => {
                      const categoryParam = selectedCategory === 'all' ? '' : `?category=${selectedCategory}`;
                      navigate(`/doa/${doa.id}${categoryParam}`);
                    }}
                    data-testid={`doa-card-${doa.id}`}
                  >
                    <CardContent className="p-3 sm:p-4 md:p-5">
                      <div className="flex items-start gap-2.5 sm:gap-3 md:gap-4">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                          <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm sm:text-base md:text-lg group-hover:text-primary transition-colors">{isEnglish ? doa.titleEn : doa.title}</h3>
                          <p className="text-xs sm:text-sm text-muted-foreground">{isEnglish ? doa.title : doa.titleEn}</p>
                          <p className="font-arabic text-sm sm:text-base text-right mt-1.5 sm:mt-2 line-clamp-1 text-primary/80" dir="rtl">
                            {doa.arabic}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0 group-hover:text-primary group-hover:translate-x-0.5 sm:group-hover:translate-x-1 transition-all" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Doa;
